import { useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { RefreshCw } from 'lucide-react'
import { api } from '../../lib/api'
import { useAuthStore } from '../../stores/auth'
import { ETICHETE_PERIOADA, interval } from '../../lib/perioade'
import { AntetPagina } from '../../components/ui/AntetPagina'
import { Button } from '../../components/ui/Button'
import { ChipuriFiltru } from '../../components/ui/ChipuriFiltru'
import { DateRangeField } from '../../components/ui/DateField'
import { PanouLateral, SectiunePanou } from '../../components/ui/PanouLateral'
import { StareLista } from '../../components/ui/StareLista'

/*
  Operațiuni → Intrări în gestiune (Alexandru, 21.10.2026). Doar vizualizare: recepțiile de la
  furnizori și transferurile primite, citite zilnic din ExpertAccounts.
*/

type Tip = 'rec' | 't-in' | 'tc-in'
const ETICHETA_TIP: Record<Tip, string> = {
  rec: 'Recepție',
  't-in': 'Transfer',
  'tc-in': 'Transfer cu confirmare',
}

interface Doc {
  id: number
  doct: Tip
  docn: string
  docd: string
  docns: string | null
  partener: string
  gestiuneSursa: string | null
  gestiuneDest: string | null
  cantitate: number
  valoare: number
  linii: number
  /** Factura furnizorului din SPV (id), doar la recepții. */
  factura: number | null
  /** Starea plății din EA, doar la recepții; `null` când EA n-a răspuns. */
  plata: { stare: 'achitat' | 'partial' | 'neachitat'; restant: number; total: number } | null
}
interface Raspuns {
  platiCitite?: boolean
  documente: Doc[]
  peTip: Partial<Record<Tip, number>>
  gestiuni: string[]
  sincronizatLa: string | null
}
interface Detaliu extends Omit<Doc, 'linii' | 'plata'> {
  linii: {
    sku: string | null
    denumire: string
    ean: string | null
    um: string | null
    cantitate: number
    pret: number
    valoare: number
  }[]
}

const lei = (n: number) =>
  n.toLocaleString('ro-RO', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
const cant = (n: number) => n.toLocaleString('ro-RO', { maximumFractionDigits: 3 })
const zi = (iso: string) => iso.split('-').reverse().join('.')

export function IntrariGestiunePage() {
  const [param, setParam] = useSearchParams()
  const eAdmin = useAuthStore((s) => s.user?.role === 'admin')
  const qc = useQueryClient()
  const implicit = useMemo(() => interval('luna_curenta'), [])
  const deLa = param.get('de_la') ?? implicit.from
  const panaLa = param.get('pana_la') ?? implicit.to
  const [vedere, setVedere] = useState<'receptii' | 'transferuri'>('receptii')
  const [tip, setTip] = useState<'toate' | Tip>('toate')
  const [gestiune, setGestiune] = useState('')
  const [cauta, setCauta] = useState('')
  const [deschis, setDeschis] = useState<number | null>(null)

  const seteaza = (de: string, pana: string) => {
    const p = new URLSearchParams(param)
    p.set('de_la', de)
    p.set('pana_la', pana)
    setParam(p, { replace: true })
  }
  const scurtaturi = (
    ['luna_curenta', 'luna_precedenta', 'an_curent', 'an_precedent'] as const
  ).map((n) => ({ cheie: n, eticheta: ETICHETE_PERIOADA[n], ...interval(n) }))

  const q = useQuery<Raspuns>({
    queryKey: ['intrari', deLa, panaLa, vedere, tip, gestiune, cauta],
    queryFn: async () =>
      (
        await api.get('/intrari', {
          params: {
            de_la: deLa,
            pana_la: panaLa,
            ...(vedere === 'receptii'
              ? { grup: 'receptii' }
              : tip !== 'toate'
                ? { tip }
                : { grup: 'transferuri' }),
            ...(vedere === 'transferuri' && gestiune ? { gestiune } : {}),
            ...(cauta.trim() ? { q: cauta.trim() } : {}),
          },
        })
      ).data,
  })
  const sincronizeaza = useMutation({
    mutationFn: async () =>
      (await api.post('/intrari/sincronizeaza', {}, { timeout: 600_000 })).data,
    onSuccess: (r: { rezultat: { documente: number } }) => {
      toast.success(
        `Sincronizare gata: ${r.rezultat.documente} documente citite din ExpertAccounts.`,
      )
      void qc.invalidateQueries({ queryKey: ['intrari'] })
    },
    onError: () => toast.error('Sincronizarea nu a reușit. Încearcă din nou peste câteva minute.'),
  })

  const docs = q.data?.documente ?? []
  const pt = q.data?.peTip ?? {}
  const transferuri = (pt['t-in'] ?? 0) + (pt['tc-in'] ?? 0)

  return (
    <div className="grid gap-4">
      <AntetPagina
        titlu="Intrări în gestiune"
        descriere="Recepțiile de la furnizori, cu factura din SPV și plata ei, și separat transferurile între gestiuni. Citite zilnic din ExpertAccounts. Doar vizualizare."
        actiuni={
          eAdmin && (
            <Button
              variant="secondary"
              onClick={() => sincronizeaza.mutate()}
              loading={sincronizeaza.isPending}
            >
              <RefreshCw size={14} className="mr-1.5" />
              Sincronizează acum
            </Button>
          )
        }
      />

      <div className="flex flex-wrap items-center gap-2 text-corp">
        <div className="flex flex-wrap gap-1.5" role="group" aria-label="Perioada">
          {scurtaturi.map((sc) => (
            <button
              key={sc.cheie}
              type="button"
              onClick={() => seteaza(sc.from, sc.to)}
              className={`rounded-caseta border px-3 py-1.5 ${
                sc.from === deLa && sc.to === panaLa
                  ? 'border-b4l-blue bg-b4l-blue font-medium text-white'
                  : 'border-b4l-border bg-b4l-surface text-b4l-text hover:border-b4l-blue'
              }`}
            >
              {sc.eticheta}
            </button>
          ))}
        </div>
        <div className="w-full sm:w-72">
          <DateRangeField
            fromValue={deLa}
            toValue={panaLa}
            onChange={(a, b) => a && b && seteaza(a, b)}
          />
        </div>
      </div>

      <ChipuriFiltru<'receptii' | 'transferuri'>
        eticheta="Ce vezi"
        ales={vedere}
        onAlege={(v) => {
          setVedere(v)
          setTip('toate')
          setDeschis(null)
        }}
        chipuri={[
          { cheie: 'receptii', eticheta: 'Recepții de la furnizori', numar: pt.rec ?? 0 },
          { cheie: 'transferuri', eticheta: 'Transferuri', numar: transferuri },
        ]}
      />

      <div className="flex flex-wrap items-center gap-2">
        {vedere === 'transferuri' && (
          <>
            <ChipuriFiltru<'toate' | Tip>
              eticheta="Tip"
              ales={tip}
              onAlege={setTip}
              chipuri={[
                { cheie: 'toate', eticheta: 'Toate', numar: transferuri },
                { cheie: 't-in', eticheta: 'Transferuri', numar: pt['t-in'] ?? 0 },
                {
                  cheie: 'tc-in',
                  eticheta: 'Transferuri cu confirmare',
                  numar: pt['tc-in'] ?? 0,
                },
              ]}
            />
            <select
              value={gestiune}
              onChange={(e) => setGestiune(e.target.value)}
              aria-label="Gestiunea"
              className="rounded-caseta border border-b4l-border bg-b4l-surface px-2 py-1.5 text-corp"
            >
              <option value="">Toate gestiunile</option>
              {(q.data?.gestiuni ?? []).map((g) => (
                <option key={g} value={g}>
                  {g}
                </option>
              ))}
            </select>
          </>
        )}
        <input
          value={cauta}
          onChange={(e) => setCauta(e.target.value)}
          placeholder={
            vedere === 'receptii'
              ? 'Caută furnizor, factură, produs, EAN'
              : 'Caută document, gestiune, produs, EAN'
          }
          className="min-w-[260px] flex-1 rounded-caseta border border-b4l-border bg-b4l-surface px-3 py-1.5 text-corp"
        />
      </div>

      <StareLista
        interogare={q}
        gol={docs.length === 0}
        mesajGol="Nicio intrare în gestiune în perioada aleasă."
      >
        <div className="overflow-x-auto rounded-xl border border-b4l-border bg-b4l-surface">
          {vedere === 'receptii' ? (
            <TabelReceptii
              docs={docs}
              deschis={deschis}
              onDeschide={setDeschis}
              platiCitite={q.data?.platiCitite !== false}
            />
          ) : (
            <TabelTransferuri docs={docs} deschis={deschis} onDeschide={setDeschis} />
          )}
          <p className="m-0 px-3.5 py-2.5 text-mic text-b4l-text-dim">
            {vedere === 'receptii'
              ? 'Valoarea e la prețul de intrare, fără TVA. Factura se deschide din SPV; plata vine din soldurile pe documente la furnizori din ExpertAccounts (contul 401), citite la câteva minute.'
              : 'Valoarea e la prețul de intrare, fără TVA.'}
            {q.data?.sincronizatLa
              ? ` Ultima citire din ExpertAccounts: ${new Date(q.data.sincronizatLa).toLocaleString('ro-RO')}.`
              : ''}
          </p>
        </div>
      </StareLista>

      {deschis != null && <PanouDocument id={deschis} laInchidere={() => setDeschis(null)} />}
    </div>
  )
}

function PanouDocument({ id, laInchidere }: { id: number; laInchidere: () => void }) {
  const q = useQuery<{ document: Detaliu }>({
    queryKey: ['intrare', id],
    queryFn: async () => (await api.get(`/intrari/${id}`)).data,
  })
  const d = q.data?.document
  return (
    <PanouLateral
      deschis
      onInchide={laInchidere}
      titlu={d ? `${ETICHETA_TIP[d.doct]} ${d.docn}` : 'Document'}
      subtitlu={d ? `${zi(d.docd)} · ${d.partener}` : undefined}
    >
      {d && (
        <div className="grid gap-4 text-corp">
          <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1">
            {d.docns && (
              <>
                <dt className="text-b4l-text-muted">
                  {d.doct === 'tc-in' ? 'Livrarea' : 'Referință'}
                </dt>
                <dd className="m-0">{d.docns}</dd>
              </>
            )}
            {d.gestiuneSursa && (
              <>
                <dt className="text-b4l-text-muted">Din gestiunea</dt>
                <dd className="m-0">{d.gestiuneSursa}</dd>
              </>
            )}
            <dt className="text-b4l-text-muted">În gestiunea</dt>
            <dd className="m-0">{d.gestiuneDest ?? '—'}</dd>
            <dt className="text-b4l-text-muted">Total</dt>
            <dd className="m-0">
              {cant(d.cantitate)} buc. · {lei(d.valoare)} lei fără TVA
            </dd>
          </dl>
          {d.doct === 'rec' && (
            <div>
              {d.factura ? (
                <Button variant="secondary" onClick={() => void deschideFactura(d.id)}>
                  Vezi factura din SPV
                </Button>
              ) : (
                <p className="m-0 text-mic text-b4l-text-muted">
                  Factura {d.docn} nu e (încă) în SPV.
                </p>
              )}
            </div>
          )}
          <SectiunePanou titlu={`Produse (${d.linii.length})`}>
            <ul className="m-0 grid gap-2 p-0">
              {d.linii.map((l, i) => (
                <li key={i} className="list-none rounded-caseta border border-b4l-border px-3 py-2">
                  <div className="font-medium text-b4l-text">{l.denumire}</div>
                  <div className="text-mic text-b4l-text-muted">
                    {[l.sku, l.ean].filter(Boolean).join(' · ')}
                  </div>
                  <div className="mt-0.5 tabular-nums">
                    {cant(l.cantitate)} {l.um ?? 'buc'} × {lei(l.pret)} = {lei(l.valoare)} lei
                  </div>
                </li>
              ))}
            </ul>
          </SectiunePanou>
        </div>
      )}
    </PanouLateral>
  )
}

const TH = 'border-b border-b4l-border px-2 py-2.5 font-medium'
const TD = 'border-b border-b4l-border px-2 py-2'

/** Deschide PDF-ul facturii din SPV intr-un tab nou (cererea poarta autentificarea). */
async function deschideFactura(id: number) {
  const tab = window.open('', '_blank')
  try {
    const r = await api.get(`/intrari/${id}/factura.pdf`, {
      responseType: 'blob',
      timeout: 60_000,
    })
    const url = URL.createObjectURL(r.data as Blob)
    if (tab) tab.location.href = url
    else window.location.href = url
  } catch {
    tab?.close()
    toast.error('Factura nu s-a putut deschide din SPV.')
  }
}

function Plata({ p, citite }: { p: Doc['plata']; citite: boolean }) {
  if (!p) {
    return (
      <span className="text-b4l-text-dim" title={citite ? '' : 'ExpertAccounts n-a răspuns'}>
        {citite ? '—' : 'necunoscut'}
      </span>
    )
  }
  if (p.stare === 'achitat')
    return (
      <span className="rounded-full bg-green-100 px-2 py-0.5 text-mic text-green-700">Achitat</span>
    )
  if (p.stare === 'partial')
    return (
      <span
        className="rounded-full bg-amber-100 px-2 py-0.5 text-mic text-amber-700"
        title={`Din ${lei(p.total)} lei a mai rămas de plătit ${lei(p.restant)} lei.`}
      >
        Parțial · rest {lei(p.restant)}
      </span>
    )
  return (
    <span className="rounded-full bg-red-100 px-2 py-0.5 text-mic text-red-700">
      Neachitat · {lei(p.restant)}
    </span>
  )
}

function TabelReceptii({
  docs,
  deschis,
  onDeschide,
  platiCitite,
}: {
  docs: Doc[]
  deschis: number | null
  onDeschide: (id: number) => void
  platiCitite: boolean
}) {
  return (
    <table className="w-full min-w-[820px] border-collapse text-corp">
      <thead>
        <tr className="text-left text-mic text-b4l-text-muted">
          <th className={`${TH} px-3.5`}>Data</th>
          <th className={TH}>Partener</th>
          <th className={TH}>Document</th>
          <th className={TH}>Factură</th>
          <th className={TH}>Plată</th>
          <th className={`${TH} px-3.5 text-right`}>Valoare (lei)</th>
        </tr>
      </thead>
      <tbody>
        {docs.map((d) => (
          <tr
            key={d.id}
            onClick={() => onDeschide(d.id)}
            className={`cursor-pointer hover:bg-b4l-bg-soft ${deschis === d.id ? 'bg-b4l-bg-soft' : ''}`}
          >
            <td className={`${TD} px-3.5 tabular-nums`}>{zi(d.docd)}</td>
            <td className={TD}>{d.partener}</td>
            <td className={`${TD} font-medium`}>{d.docn}</td>
            <td className={TD}>
              {d.factura ? (
                <button
                  type="button"
                  className="text-b4l-blue hover:underline"
                  onClick={(e) => {
                    e.stopPropagation()
                    void deschideFactura(d.id)
                  }}
                >
                  Vezi factura
                </button>
              ) : (
                <span className="text-b4l-text-dim" title="Factura nu e (încă) în SPV">
                  nu e în SPV
                </span>
              )}
            </td>
            <td className={TD}>
              <Plata p={d.plata} citite={platiCitite} />
            </td>
            <td className={`${TD} px-3.5 text-right tabular-nums`}>{lei(d.valoare)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}

function TabelTransferuri({
  docs,
  deschis,
  onDeschide,
}: {
  docs: Doc[]
  deschis: number | null
  onDeschide: (id: number) => void
}) {
  return (
    <table className="w-full min-w-[820px] border-collapse text-corp">
      <thead>
        <tr className="text-left text-mic text-b4l-text-muted">
          <th className={`${TH} px-3.5`}>Data</th>
          <th className={TH}>Tip</th>
          <th className={TH}>Document</th>
          <th className={TH}>Din gestiunea</th>
          <th className={TH}>În gestiunea</th>
          <th className={`${TH} text-right`}>Cantitate</th>
          <th className={`${TH} px-3.5 text-right`}>Valoare (lei)</th>
        </tr>
      </thead>
      <tbody>
        {docs.map((d) => (
          <tr
            key={d.id}
            onClick={() => onDeschide(d.id)}
            className={`cursor-pointer hover:bg-b4l-bg-soft ${deschis === d.id ? 'bg-b4l-bg-soft' : ''}`}
          >
            <td className={`${TD} px-3.5 tabular-nums`}>{zi(d.docd)}</td>
            <td className={TD}>{ETICHETA_TIP[d.doct]}</td>
            <td className={`${TD} font-medium`}>
              {d.docn}
              {d.docns && <span className="ml-1 text-mic text-b4l-text-muted">· {d.docns}</span>}
            </td>
            <td className={TD}>{d.gestiuneSursa ?? d.partener}</td>
            <td className={TD}>{d.gestiuneDest ?? <span className="text-b4l-text-dim">—</span>}</td>
            <td className={`${TD} text-right tabular-nums`}>{cant(d.cantitate)}</td>
            <td className={`${TD} px-3.5 text-right tabular-nums`}>{lei(d.valoare)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}
